import { useEffect, useState, useCallback, useRef } from 'react';
import { contentAPI, usersAPI } from '../services/api';
import { Table, Badge, Btn, Modal, Card, Pagination, useToast, Input, Select, Spinner } from '../components/UI';

function formatBytes(b) {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(mime) {
  if (!mime) return '📄';
  if (mime.startsWith('audio/'))       return '🎵';
  if (mime.startsWith('image/'))       return '🖼';
  if (mime === 'application/pdf')      return '📕';
  if (mime.startsWith('video/'))       return '🎬';
  if (mime.includes('text'))           return '📝';
  return '📄';
}

function fileCategory(mime) {
  if (!mime) return 'other';
  if (mime.startsWith('audio/'))  return 'audio';
  if (mime.startsWith('image/'))  return 'image';
  if (mime === 'application/pdf') return 'pdf';
  if (mime.startsWith('video/'))  return 'video';
  return 'other';
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Content() {
  const { show, ToastEl } = useToast();
  const [files, setFiles]         = useState([]);
  const [categories, setCats]     = useState([]);
  const [roles, setRoles]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(1);
  const [meta, setMeta]           = useState({});
  const [catFilter, setCatFilter] = useState('');
  const [showUpload, setShowUpload]     = useState(false);
  const [showCatMgr, setShowCatMgr]   = useState(false);
  const [showPrivate, setShowPrivate]  = useState(false);
  const [editFile, setEditFile]         = useState(null);
  const [viewFile, setViewFile]         = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await contentAPI.files({ page, limit: 15, category_id: catFilter, exclude_media: true });
      setFiles(data.data);
      setMeta(data.pagination);
    } catch { show('Failed to load files', 'error'); }
    finally { setLoading(false); }
  }, [page, catFilter]);

  const loadCats = useCallback(() => {
    contentAPI.categories().then(r => {
      const excluded = ['audio', 'video'];
      setCats(r.data.data.filter(c => !excluded.includes(c.name?.toLowerCase())));
    });
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    loadCats();
    usersAPI.listRoles().then(r => setRoles(r.data.data));
  }, []);

  async function deleteFile(id) {
    if (!window.confirm('Delete this file? This cannot be undone.')) return;
    try {
      await contentAPI.deleteFile(id);
      show('File deleted');
      load(); loadCats();
    } catch { show('Failed to delete file', 'error'); }
  }

  const columns = [
    { key: 'title', label: 'Title', render: (v, row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 18, flexShrink: 0 }}>{fileIcon(row.file_type)}</span>
        <div>
          <div style={{ fontWeight: 500 }}>{v}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{row.category_name}</div>
        </div>
      </div>
    )},
    { key: 'file_type', label: 'Type', render: v => (
      <span style={{ fontFamily: 'var(--mono)', fontSize: 11, background: 'var(--surface2)', padding: '2px 7px', borderRadius: 4 }}>
        {v ? v.split('/')[1] : '—'}
      </span>
    )},
    { key: 'file_size_bytes', label: 'Size',     render: v => formatBytes(v) },
    { key: 'status',          label: 'Status',   render: v => <Badge label={v} type={v}/> },
    { key: 'uploaded_by',     label: 'By',       render: v => <span style={{ fontSize: 12 }}>{v}</span> },
    { key: 'created_at',      label: 'Uploaded', render: v => new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) },
    { key: 'actions', label: '', render: (_, row) => (
      <div style={{ display: 'flex', gap: 6 }}>
        <Btn size="sm" variant="outline"  onClick={() => setViewFile(row)}>View</Btn>
        <Btn size="sm" variant="ghost"    onClick={() => setEditFile(row)}>Edit</Btn>
        <Btn size="sm" variant="danger"   onClick={() => deleteFile(row.id)}>Delete</Btn>
      </div>
    )},
  ];

  return (
    <div style={{ padding: '28px 32px', animation: 'fadeIn .35s ease' }}>
      {ToastEl}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: 26, fontWeight: 700 }}>Content</h1>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>Upload and manage role-gated content files.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="ghost"   onClick={() => setShowPrivate(true)}>🔒 Private Content</Btn>
          <Btn variant="ghost"   onClick={() => setShowCatMgr(true)}>⚙ Categories</Btn>
          <Btn variant="primary" onClick={() => setShowUpload(true)}>Upload file</Btn>
        </div>
      </div>

      {/* Category pills */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {[{ id: '', name: 'All' }, ...categories].map(c => (
          <button key={c.id} onClick={() => { setCatFilter(c.id); setPage(1); }} style={{
            padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500,
            border: '1px solid var(--border)',
            background: catFilter === c.id ? 'var(--accent)' : 'transparent',
            color: catFilter === c.id ? '#fff' : 'var(--text2)', cursor: 'pointer',
          }}>
            {c.name}{c.file_count != null ? ` (${c.file_count})` : ''}
          </button>
        ))}
      </div>

      <Card>
        <Table columns={columns} data={files} loading={loading}/>
        <Pagination page={page} pages={meta.pages} total={meta.total} limit={15} onChange={setPage}/>
      </Card>

      {/* View modal */}
      {viewFile && (
        <ViewModal
          file={viewFile}
          onClose={() => setViewFile(null)}
          onEdit={() => { setViewFile(null); setEditFile(viewFile); }}
        />
      )}

      {/* Upload modal */}
      {showUpload && (
        <UploadModal
          categories={categories} roles={roles}
          onClose={() => setShowUpload(false)}
          onSuccess={() => { setShowUpload(false); load(); loadCats(); show('File uploaded successfully'); }}
          onError={msg => show(msg, 'error')}
        />
      )}

      {/* Edit modal */}
      {editFile && (
        <EditModal
          file={editFile} roles={roles} categories={categories}
          onClose={() => setEditFile(null)}
          onSuccess={() => { setEditFile(null); load(); loadCats(); show('File updated'); }}
          onError={msg => show(msg, 'error')}
        />
      )}

      {/* Category manager */}
      {showCatMgr && (
        <ManageCategoriesModal
          onClose={() => setShowCatMgr(false)}
          onChanged={() => contentAPI.categories().then(r => {
            const excluded = ['audio', 'video'];
            setCats(r.data.data.filter(c => !excluded.includes(c.name?.toLowerCase())));
          })}
        />
      )}

      {/* Private content manager */}
      {showPrivate && (
        <PrivateContentModal onClose={() => setShowPrivate(false)} />
      )}
    </div>
  );
}

// ── View Modal ────────────────────────────────────────────────────────────────
function ViewModal({ file, onClose, onEdit }) {
  const hasInlineText           = Boolean(file.body_text);
  const [blobUrl, setBlobUrl]   = useState(null);
  const [loading, setLoading]   = useState(!hasInlineText);
  const [error, setError]       = useState('');
  const audioRef                = useRef(null);
  const token                   = localStorage.getItem('accessToken');
  const streamUrl               = `${process.env.REACT_APP_API_URL || ''}/api/content/files/${file.id}/stream`;
  const kind                    = fileCategory(file.file_type);

  useEffect(() => {
    if (hasInlineText) return; // body_text available — no stream fetch needed

    let objectUrl = null;

    async function fetchBlob() {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(streamUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const json = await res.json().catch(() => ({}));
          throw new Error(json.message || `HTTP ${res.status}`);
        }
        const blob = await res.blob();
        const serverMime = res.headers.get('content-type')?.split(';')[0].trim();
        const mimeType   = serverMime || file.file_type || blob.type || 'application/octet-stream';
        const typedBlob  = new Blob([blob], { type: mimeType });
        objectUrl = URL.createObjectURL(typedBlob);
        setBlobUrl(objectUrl);
      } catch (e) {
        setError(e.message || 'Failed to load file');
      } finally {
        setLoading(false);
      }
    }

    fetchBlob();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file.id, hasInlineText]);

  function handleDownload() {
    if (!blobUrl) return;
    const a    = document.createElement('a');
    a.href     = blobUrl;
    a.download = file.title || 'download';
    a.click();
  }

  return (
    <Modal title="" onClose={onClose} width={700}>
      {/* Custom header */}
      <div style={{ margin: '-20px -20px 16px', padding: '14px 20px', borderBottom: '1px solid var(--border)', background: 'var(--surface2)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 24 }}>{fileIcon(file.file_type)}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {file.title}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
            {file.category_name}
            {hasInlineText
              ? ` · ${file.body_text.length.toLocaleString()} chars · text`
              : ` · ${formatBytes(file.file_size_bytes)} · ${file.file_type || 'unknown type'}`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <Btn size="sm" variant="ghost" onClick={onEdit}>Edit</Btn>
          {!hasInlineText && (
            <Btn size="sm" variant="outline" onClick={handleDownload} disabled={!blobUrl}>Download</Btn>
          )}
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text3)', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>
      </div>

      {/* Content area */}
      <div style={{ minHeight: 260 }}>

        {/* ── Inline text content ── */}
        {hasInlineText && (
          <div style={{ padding: '16px 0' }}>
            <InlineTextViewer text={file.body_text}/>
            <FileMetaRow file={file} isText/>
          </div>
        )}

        {/* ── File-based content ── */}
        {!hasInlineText && loading && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: 12 }}>
            <Spinner size={28}/>
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>Loading file…</span>
          </div>
        )}

        {!hasInlineText && !loading && error && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: 10 }}>
            <span style={{ fontSize: 32 }}>⚠️</span>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--red)' }}>Could not load file</div>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>{error}</div>
          </div>
        )}

        {!hasInlineText && !loading && !error && blobUrl && (
          kind === 'audio' ? (
            <div style={{ padding: '32px 20px' }}>
              <div style={{ background: 'var(--surface2)', borderRadius: 'var(--r2)', padding: '28px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 56, marginBottom: 16 }}>🎵</div>
                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>{file.title}</div>
                {file.description && (
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>{file.description}</div>
                )}
                <audio ref={audioRef} controls src={blobUrl} style={{ width: '100%', borderRadius: 8, outline: 'none', marginTop: 8 }}>
                  Your browser does not support audio playback.
                </audio>
              </div>
              <FileMetaRow file={file}/>
            </div>

          ) : kind === 'image' ? (
            <div style={{ padding: '16px 0' }}>
              <div style={{ textAlign: 'center', background: 'var(--surface2)', borderRadius: 'var(--r)', overflow: 'hidden', maxHeight: 460, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={blobUrl} alt={file.title} style={{ maxWidth: '100%', maxHeight: 460, objectFit: 'contain', display: 'block' }}/>
              </div>
              <FileMetaRow file={file}/>
            </div>

          ) : kind === 'pdf' ? (
            <div style={{ padding: '0' }}>
              <PdfViewer blobUrl={blobUrl} title={file.title}/>
              <FileMetaRow file={file}/>
            </div>

          ) : kind === 'video' ? (
            <div style={{ padding: '16px 0' }}>
              <video controls src={blobUrl} style={{ width: '100%', borderRadius: 'var(--r)', background: '#000', maxHeight: 420 }}>
                Your browser does not support video playback.
              </video>
              <FileMetaRow file={file}/>
            </div>

          ) : (
            <div style={{ padding: '20px 0' }}>
              <TextFileViewer blobUrl={blobUrl} mimeType={file.file_type}/>
              <FileMetaRow file={file}/>
            </div>
          )
        )}
      </div>
    </Modal>
  );
}

// ── Inline text viewer (body_text stored directly in DB) ─────────────────────
function InlineTextViewer({ text }) {
  return (
    <div style={{
      maxHeight: 460, overflowY: 'auto',
      background: 'var(--surface2)', borderRadius: 'var(--r)',
      padding: '20px 22px',
    }}>
      <p dir="auto" style={{
        margin: 0, fontSize: 15, lineHeight: 2,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        color: 'var(--text)', fontFamily: 'var(--sans)',
      }}>
        {text}
      </p>
    </div>
  );
}

// ── PDF viewer ───────────────────────────────────────────────────────────────
// Uses <embed> instead of <iframe> — more reliable for blob: URLs across browsers
function PdfViewer({ blobUrl, title }) {
  const [supported, setSupported] = useState(true);

  // Detect if browser can render PDF inline (Chromium and Firefox both support it natively)
  useEffect(() => {
    const isChromium = !!(window.chrome);
    const isFirefox  = navigator.userAgent.includes('Firefox');
    if (!isChromium && !isFirefox) setSupported(false);
  }, []);

  if (!supported) {
    return (
      <div style={{
        textAlign: 'center', padding: '40px 20px',
        background: 'var(--surface2)', borderRadius: 'var(--r)',
      }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>📕</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>
          PDF preview is not supported in this browser.
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)' }}>
          Use the Download button to open the file.
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: 520, borderRadius: 'var(--r)', overflow: 'hidden', background: '#525659' }}>
      <embed
        src={blobUrl}
        type="application/pdf"
        title={title}
        style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
      />
    </div>
  );
}

// ── Text file viewer ──────────────────────────────────────────────────────────
function TextFileViewer({ blobUrl, mimeType }) {
  const [text, setText] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!blobUrl) return;
    if (mimeType && mimeType.includes('text')) {
      fetch(blobUrl).then(r => r.text()).then(t => { setText(t); setLoaded(true); });
    } else {
      setLoaded(true);
    }
  }, [blobUrl, mimeType]);

  if (!loaded) return <div style={{ padding: 20, textAlign: 'center' }}><Spinner/></div>;

  if (text) return (
    <pre style={{
      fontFamily: 'var(--mono)', fontSize: 12, lineHeight: 1.7,
      background: 'var(--surface2)', borderRadius: 'var(--r)',
      padding: '16px 18px', overflowX: 'auto', whiteSpace: 'pre-wrap',
      wordBreak: 'break-word', maxHeight: 400, overflowY: 'auto',
      color: 'var(--text)',
    }}>{text}</pre>
  );

  return (
    <div style={{
      textAlign: 'center', padding: '40px 20px',
      background: 'var(--surface2)', borderRadius: 'var(--r)',
    }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>📄</div>
      <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 4 }}>This file type cannot be previewed in the browser.</div>
      <div style={{ fontSize: 11, color: 'var(--text3)' }}>Use the Download button to open it on your device.</div>
    </div>
  );
}

// ── File metadata row shown below every preview ───────────────────────────────
function FileMetaRow({ file, isText = false }) {
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 20, marginTop: 14,
      padding: '12px 0 0', borderTop: '1px solid var(--border)',
    }}>
      {[
        { label: 'Category',    value: file.category_name },
        !isText && { label: 'Size', value: formatBytes(file.file_size_bytes) },
        { label: 'Status',      value: <Badge label={file.status} type={file.status}/> },
        { label: 'Uploaded',    value: new Date(file.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) },
        { label: 'By',          value: file.uploaded_by },
        ...(file.description ? [{ label: 'Description', value: file.description }] : []),
      ].filter(Boolean).map(({ label, value }) => (
        <div key={label}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 3 }}>{label}</div>
          <div style={{ fontSize: 13, color: 'var(--text)' }}>{value}</div>
        </div>
      ))}
    </div>
  );
}

// ── Upload Modal ──────────────────────────────────────────────────────────────
function UploadModal({ categories, roles, onClose, onSuccess, onError }) {
  const [form, setForm]     = useState({ title: '', description: '', body_text: '', category_id: '', sort_order: 0, roleIds: [] });
  const [file, setFile]     = useState(null);
  const [saving, setSaving] = useState(false);

  const hasFile = Boolean(file);

  function toggleRole(id, checked) {
    const rid = Number(id);
    setForm(f => ({ ...f, roleIds: checked ? [...f.roleIds, rid] : f.roleIds.filter(x => x !== rid) }));
  }

  const isPrivateCat = categories.find(c => String(c.id) === String(form.category_id))?.slug === 'private';

  async function submit() {
    if (!form.title.trim())                      return onError('Title is required');
    if (!form.category_id)                       return onError('Please select a category');
    if (!isPrivateCat && !form.roleIds.length)   return onError('Select at least one role');

    setSaving(true);
    try {
      if (hasFile) {
        const slug = categories.find(c => String(c.id) === String(form.category_id))?.slug || 'general';
        const fd   = new FormData();
        fd.append('title',         form.title);
        fd.append('description',   form.description);
        fd.append('category_id',   form.category_id);
        fd.append('category_slug', slug);
        fd.append('sort_order',    form.sort_order);
        form.roleIds.forEach(rid => fd.append('roleIds[]', rid));
        fd.append('file', file);
        await contentAPI.uploadFile(fd, slug);
      } else {
        if (!form.body_text.trim()) return onError('Please enter text content or upload a file');
        await contentAPI.createText({
          title:       form.title.trim(),
          description: form.description || undefined,
          body_text:   form.body_text,
          category_id: form.category_id,
          roleIds:     form.roleIds,
          sort_order:  form.sort_order,
        });
      }
      onSuccess();
    } catch (e) { onError(e.response?.data?.message || 'Failed to save content'); }
    finally { setSaving(false); }
  }

  return (
    <Modal title="Add content" onClose={onClose} width={540}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

        <Input label="Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Subhanallahi Wabihamdihi"/>
        <Input label="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}/>

        {/* File upload — optional */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)', display: 'block', marginBottom: 5 }}>
            File <span style={{ fontWeight: 400, color: 'var(--text3)' }}>(optional — leave empty to add text content below)</span>
          </label>
          <input type="file" onChange={e => setFile(e.target.files[0] || null)} style={{ fontSize: 13 }}/>
          {hasFile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 12, color: 'var(--text3)' }}>
              <span>{fileIcon(file.type)}</span>
              <span>{file.name}</span>
              <span>·</span>
              <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
              <button onClick={() => setFile(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 13, padding: 0 }}>✕ Remove</button>
            </div>
          )}
        </div>

        {/* Text area — shown only when no file selected */}
        {!hasFile && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)' }}>
              Content text * <span style={{ fontWeight: 400, color: 'var(--text3)' }}>(Arabic, English, Malayalam, Urdu, Tamil supported)</span>
            </label>
            <textarea
              dir="auto"
              placeholder="Type or paste your text here…"
              value={form.body_text}
              onChange={e => setForm(f => ({ ...f, body_text: e.target.value }))}
              rows={8}
              style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--r)', padding: '10px 12px', fontSize: 14,
                color: 'var(--text)', outline: 'none', width: '100%',
                resize: 'vertical', fontFamily: 'var(--sans)', lineHeight: 1.9,
              }}
            />
          </div>
        )}

        <Select label="Category *" value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}>
          <option value="">Select category…</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>

        <Input label="Sort order" value={form.sort_order} type="number"
          onChange={e => setForm(f => ({ ...f, sort_order: Number(e.target.value) }))}
        />

        {!isPrivateCat && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)', marginBottom: 8 }}>Visible to roles *</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {roles.filter(r => r.name !== 'Admin').map(r => {
                const checked = form.roleIds.includes(Number(r.id));
                return (
                  <label key={r.id} style={{
                    display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer',
                    background: checked ? 'var(--accent-light)' : 'var(--surface2)',
                    border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: 'var(--r)', padding: '5px 12px', transition: 'all .12s',
                  }}>
                    <input type="checkbox" style={{ accentColor: 'var(--accent)' }}
                      checked={checked} onChange={e => toggleRole(r.id, e.target.checked)}
                    />
                    {r.name}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {isPrivateCat && (
          <div style={{ fontSize: 12, color: 'var(--text3)', background: 'var(--surface2)', borderRadius: 'var(--r)', padding: '10px 12px' }}>
            🔒 Private files are not visible to any role. Use <strong>Private Content → Manage Access</strong> to assign individual users after upload.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <Btn onClick={submit} disabled={saving}>
            {saving ? <Spinner size={14} color="#fff"/> : (hasFile ? 'Upload' : 'Save')}
          </Btn>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ── Edit Modal ────────────────────────────────────────────────────────────────
function EditModal({ file, roles, categories, onClose, onSuccess, onError }) {
  const isTextContent  = file.body_text != null;
  const initialRoleIds = Array.isArray(file.role_ids) ? file.role_ids.map(Number) : [];
  const [form, setForm] = useState({
    title:       file.title,
    description: file.description || '',
    body_text:   file.body_text   || '',
    status:      file.status,
    category_id: String(file.category_id || ''),
    sort_order:  file.sort_order ?? 0,
    roleIds:     initialRoleIds,
  });
  const [saving, setSaving] = useState(false);

  const isPrivateCat = categories.find(c => String(c.id) === String(form.category_id))?.slug === 'private';

  function toggleRole(id, checked) {
    const rid = Number(id);
    setForm(f => ({ ...f, roleIds: checked ? [...f.roleIds, rid] : f.roleIds.filter(x => x !== rid) }));
  }

  async function submit() {
    if (!form.category_id) return onError('Please select a category');
    setSaving(true);
    try {
      const payload = {
        title:       form.title,
        description: form.description,
        status:      form.status,
        category_id: form.category_id,
        sort_order:  form.sort_order,
        roleIds:     form.roleIds.length ? form.roleIds : undefined,
        ...(isTextContent && { body_text: form.body_text }),
      };
      await contentAPI.updateFile(file.id, payload);
      onSuccess();
    } catch (e) { onError(e.response?.data?.message || 'Update failed'); }
    finally { setSaving(false); }
  }

  return (
    <Modal title="Edit content" onClose={onClose} width={500}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Current content badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface2)', borderRadius: 'var(--r)', padding: '10px 14px' }}>
          <span style={{ fontSize: 22 }}>{fileIcon(file.file_type)}</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{file.title}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              {isTextContent ? `Text · ${file.body_text.length.toLocaleString()} chars` : `${formatBytes(file.file_size_bytes)} · ${file.file_type || '—'}`}
            </div>
          </div>
        </div>

        <Input label="Title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}/>
        <Input label="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}/>

        {isTextContent && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)' }}>Content text</label>
            <textarea
              dir="auto"
              value={form.body_text}
              onChange={e => setForm(f => ({ ...f, body_text: e.target.value }))}
              rows={8}
              style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--r)', padding: '10px 12px', fontSize: 14,
                color: 'var(--text)', outline: 'none', width: '100%',
                resize: 'vertical', fontFamily: 'var(--sans)', lineHeight: 1.9,
              }}
            />
          </div>
        )}

        <Select label="Status" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
        </Select>

        <Select label="Category *" value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}>
          <option value="">Select category…</option>
          {categories.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
        </Select>

        <Input label="Sort order" value={form.sort_order} type="number"
          onChange={e => setForm(f => ({ ...f, sort_order: Number(e.target.value) }))}
        />

        {!isPrivateCat && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)', marginBottom: 8 }}>Role access</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {roles.filter(r => r.name !== 'Admin').map(r => {
                const checked = form.roleIds.includes(Number(r.id));
                return (
                  <label key={r.id} style={{
                    display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer',
                    background: checked ? 'var(--accent-light)' : 'var(--surface2)',
                    border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: 'var(--r)', padding: '5px 12px', transition: 'all .12s',
                  }}>
                    <input type="checkbox" style={{ accentColor: 'var(--accent)' }}
                      checked={checked} onChange={e => toggleRole(r.id, e.target.checked)}
                    />
                    {r.name}
                  </label>
                );
              })}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
              Changes to role access take effect immediately on save.
            </div>
          </div>
        )}
        {isPrivateCat && (
          <div style={{ fontSize: 12, color: 'var(--text3)', background: 'var(--surface2)', borderRadius: 'var(--r)', padding: '10px 14px' }}>
            🔒 Private files are not visible to any role. Use <strong>Private Content → Manage Access</strong> to assign individual users.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <Btn onClick={submit} disabled={saving}>{saving ? <Spinner size={14} color="#fff"/> : 'Save changes'}</Btn>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ── Private Content Modal ─────────────────────────────────────────────────────
// Lists all files in the private category. Each row has a "Manage Access" button
// that opens ManagePrivateAccessModal to add/remove per-user assignments.
function PrivateContentModal({ onClose }) {
  const { show, ToastEl } = useToast();
  const [files, setFiles]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage]     = useState(1);
  const [meta, setMeta]     = useState({});
  const [managingFile, setManagingFile] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await contentAPI.listPrivateFiles({ page, limit: 15 });
      setFiles(data.data);
      setMeta(data.pagination);
    } catch { show('Failed to load private files', 'error'); }
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  return (
    <Modal title="🔒 Private Content" onClose={onClose} width={680}>
      {ToastEl}
      <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
        Files here are invisible to all users by default. Assign them to individual users
        using the <strong>Manage Access</strong> button. Upload private files by choosing
        the <em>Private</em> category in the Upload dialog.
      </p>

      {loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center' }}><Spinner /></div>
      ) : files.length === 0 ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
          No private files yet. Upload a file and choose the "Private" category.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {files.map(f => (
            <div key={f.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 14px', borderRadius: 'var(--r)',
              border: '1px solid var(--border)', background: 'var(--surface)',
            }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>{fileIcon(f.file_type)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {f.title}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  {formatBytes(f.file_size_bytes)} · <Badge label={f.status} type={f.status}/>
                  <span style={{ marginLeft: 8 }}>👤 {f.user_count} user{f.user_count !== 1 ? 's' : ''} assigned</span>
                </div>
              </div>
              <Btn size="sm" onClick={() => setManagingFile(f)}>Manage Access</Btn>
            </div>
          ))}
        </div>
      )}

      {!loading && meta.pages > 1 && (
        <div style={{ marginTop: 12 }}>
          <Pagination page={page} pages={meta.pages} onChange={setPage} />
        </div>
      )}

      {managingFile && (
        <ManagePrivateAccessModal
          file={managingFile}
          onClose={() => { setManagingFile(null); load(); }}
        />
      )}
    </Modal>
  );
}

// ── Manage Private Access Modal ───────────────────────────────────────────────
// Shows users assigned to a private file; lets admin add/remove users.
function ManagePrivateAccessModal({ file, onClose }) {
  const { show, ToastEl } = useToast();
  const [assigned, setAssigned]   = useState([]);
  const [loadingA, setLoadingA]   = useState(true);
  const [search, setSearch]       = useState('');
  const [results, setResults]     = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer               = useRef(null);

  async function loadAssigned() {
    setLoadingA(true);
    try {
      const { data } = await contentAPI.getPrivateFileUsers(file.id);
      setAssigned(data.data);
    } catch { show('Failed to load assigned users', 'error'); }
    finally { setLoadingA(false); }
  }

  useEffect(() => { loadAssigned(); }, []);

  function onSearchChange(e) {
    const q = e.target.value;
    setSearch(q);
    clearTimeout(searchTimer.current);
    if (!q.trim()) { setResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await usersAPI.list({ search: q, limit: 10 });
        setResults(data.data || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 350);
  }

  async function assign(user) {
    try {
      await contentAPI.assignPrivateFile(file.id, user.id);
      setSearch(''); setResults([]);
      await loadAssigned();
      show(`${user.name} assigned`);
    } catch (e) { show(e.response?.data?.message || 'Failed to assign', 'error'); }
  }

  async function unassign(userId, userName) {
    try {
      await contentAPI.unassignPrivateFile(file.id, userId);
      await loadAssigned();
      show(`${userName} removed`);
    } catch { show('Failed to remove', 'error'); }
  }

  const assignedIds = new Set(assigned.map(u => u.id));

  return (
    <Modal title={`Access — ${file.title}`} onClose={onClose} width={520}>
      {ToastEl}

      {/* Search & add user */}
      <div style={{ position: 'relative', marginBottom: 20 }}>
        <Input
          label="Add user"
          value={search}
          onChange={onSearchChange}
          placeholder="Search by name or email…"
        />
        {(results.length > 0 || searching) && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--r)', boxShadow: '0 4px 16px #0002',
            maxHeight: 220, overflowY: 'auto',
          }}>
            {searching && (
              <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text3)' }}>Searching…</div>
            )}
            {!searching && results.map(u => {
              const alreadyAssigned = assignedIds.has(u.id);
              return (
                <button key={u.id}
                  disabled={alreadyAssigned}
                  onClick={() => !alreadyAssigned && assign(u)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                    padding: '9px 14px', border: 'none', background: 'none',
                    textAlign: 'left', cursor: alreadyAssigned ? 'default' : 'pointer',
                    opacity: alreadyAssigned ? 0.45 : 1,
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%', background: 'var(--accent)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, color: '#fff', fontWeight: 700, flexShrink: 0,
                  }}>
                    {u.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{u.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{u.email}</div>
                  </div>
                  {alreadyAssigned && (
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>assigned</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Assigned users list */}
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
        Assigned users ({assigned.length})
      </div>

      {loadingA ? (
        <div style={{ padding: '24px 0', textAlign: 'center' }}><Spinner /></div>
      ) : assigned.length === 0 ? (
        <div style={{ padding: '20px 0', textAlign: 'center', fontSize: 13, color: 'var(--text3)' }}>
          No users assigned yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {assigned.map(u => (
            <div key={u.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 12px', borderRadius: 'var(--r)',
              border: '1px solid var(--border)', background: 'var(--surface)',
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', background: 'var(--accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, color: '#fff', fontWeight: 700, flexShrink: 0,
              }}>
                {u.name?.[0]?.toUpperCase() || '?'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{u.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{u.email}</div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0 }}>
                {new Date(u.assigned_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              </div>
              <Btn size="sm" variant="danger" onClick={() => unassign(u.id, u.name)}>Remove</Btn>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ── Manage Categories Modal ───────────────────────────────────────────────────
function ManageCategoriesModal({ onClose, onChanged }) {
  const { show, ToastEl } = useToast();
  const [cats, setCats]     = useState([]);
  const [loading, setLoading] = useState(true);
  // 'list' | 'create' | 'edit'
  const [view, setView]     = useState('list');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(false);

  const emptyForm = { name: '', slug: '', description: '', parent_id: '', sort_order: 0 };
  const [form, setForm] = useState(emptyForm);

  function autoSlug(name) {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  async function load() {
    setLoading(true);
    try {
      const r = await contentAPI.categories();
      setCats(r.data.data);
    } catch { show('Failed to load categories', 'error'); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  function startCreate() {
    setForm(emptyForm);
    setEditing(null);
    setView('create');
  }

  function startEdit(cat) {
    setEditing(cat);
    setForm({ name: cat.name, slug: cat.slug, description: cat.description || '', parent_id: cat.parent_id || '', sort_order: cat.sort_order ?? 0 });
    setView('edit');
  }

  async function submitCreate() {
    if (!form.name.trim()) return show('Name is required', 'error');
    if (!form.slug.trim()) return show('Slug is required', 'error');
    setSaving(true);
    try {
      await contentAPI.createCategory({ ...form, parent_id: form.parent_id || undefined });
      show('Category created');
      onChanged();
      await load();
      setView('list');
    } catch (e) { show(e.response?.data?.message || 'Failed to create category', 'error'); }
    finally { setSaving(false); }
  }

  async function submitEdit() {
    if (!form.name.trim()) return show('Name is required', 'error');
    if (!form.slug.trim()) return show('Slug is required', 'error');
    setSaving(true);
    try {
      await contentAPI.updateCategory(editing.id, { ...form, parent_id: form.parent_id || undefined });
      show('Category updated');
      onChanged();
      await load();
      setView('list');
    } catch (e) { show(e.response?.data?.message || 'Failed to update category', 'error'); }
    finally { setSaving(false); }
  }

  async function confirmDelete() {
    setDeleting(true);
    try {
      await contentAPI.deleteCategory(confirmDel.id);
      show('Category deleted');
      onChanged();
      await load();
    } catch (e) { show(e.response?.data?.message || 'Failed to delete category', 'error'); }
    finally { setDeleting(false); setConfirmDel(null); }
  }

  const CategoryForm = (isEdit) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Input
        label="Name *" value={form.name}
        onChange={e => setForm(f => ({ ...f, name: e.target.value, slug: isEdit ? f.slug : autoSlug(e.target.value) }))}
        placeholder="e.g. Baith"
      />
      <Input
        label="Slug *" value={form.slug}
        onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
        placeholder="e.g. baith"
      />
      <Input
        label="Description" value={form.description}
        onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
      />
      <Select
        label="Parent category (optional)" value={form.parent_id}
        onChange={e => setForm(f => ({ ...f, parent_id: e.target.value }))}
      >
        <option value="">None (top level)</option>
        {cats.filter(c => !isEdit || c.id !== editing?.id).map(c => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </Select>
      <Input
        label="Sort order" value={form.sort_order} type="number"
        onChange={e => setForm(f => ({ ...f, sort_order: Number(e.target.value) }))}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <Btn onClick={isEdit ? submitEdit : submitCreate} disabled={saving}>
          {saving ? <Spinner size={14} color="#fff"/> : (isEdit ? 'Save changes' : 'Create')}
        </Btn>
        <Btn variant="ghost" onClick={() => setView('list')}>Cancel</Btn>
      </div>
    </div>
  );

  return (
    <Modal
      title={view === 'list' ? 'Manage categories' : view === 'create' ? 'New category' : `Edit — ${editing?.name}`}
      onClose={onClose}
      width={520}
    >
      {ToastEl}

      {view === 'list' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <Btn size="sm" onClick={startCreate}>+ New category</Btn>
          </div>

          {loading ? (
            <div style={{ padding: '32px 0', textAlign: 'center' }}><Spinner/></div>
          ) : cats.length === 0 ? (
            <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
              No categories yet. Create one to get started.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {cats.map(cat => (
                <div key={cat.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', borderRadius: 'var(--r)',
                  border: '1px solid var(--border)', background: 'var(--surface)',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{cat.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                      <span style={{ fontFamily: 'var(--mono)', background: 'var(--surface2)', padding: '1px 6px', borderRadius: 3 }}>{cat.slug}</span>
                      {cat.file_count != null && (
                        <span style={{ marginLeft: 8 }}>{cat.file_count} file{cat.file_count !== 1 ? 's' : ''}</span>
                      )}
                      {cat.description && (
                        <span style={{ marginLeft: 8 }}>· {cat.description}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <Btn size="sm" variant="ghost" onClick={() => startEdit(cat)}>Edit</Btn>
                    <Btn size="sm" variant="danger" onClick={() => setConfirmDel({ id: cat.id, name: cat.name })}>Delete</Btn>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === 'create' && CategoryForm(false)}
      {view === 'edit'   && CategoryForm(true)}

      {/* Delete confirmation */}
      {confirmDel && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 'var(--r2)', zIndex: 10,
        }}>
          <div style={{
            background: 'var(--surface)', borderRadius: 'var(--r2)',
            padding: '24px 28px', maxWidth: 360, width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,.18)',
          }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Delete category?</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20 }}>
              <strong>"{confirmDel.name}"</strong> will be permanently deleted. This cannot be undone.
              Categories with existing files cannot be deleted.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn variant="danger" disabled={deleting} onClick={confirmDelete}>
                {deleting ? <Spinner size={14} color="#fff"/> : 'Delete'}
              </Btn>
              <Btn variant="ghost" onClick={() => setConfirmDel(null)}>Cancel</Btn>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
